/**
 * Money is ALWAYS integer cents. Never floats, never `number` dollars.
 *
 * Every total that reaches the database must be recomputed here on the server;
 * client-supplied totals are display-only and are discarded on write.
 */

export type Cents = number;

export class MoneyError extends Error {}

/** Guard: a value that is safe to treat as cents. */
export function assertCents(value: unknown, label = 'amount'): asserts value is Cents {
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    throw new MoneyError(`${label} must be an integer number of cents, got ${String(value)}`);
  }
  if (!Number.isSafeInteger(value)) {
    throw new MoneyError(`${label} exceeds safe integer range`);
  }
}

/**
 * A line total. Quantity is a decimal (e.g. 2.5 hours) but the product is
 * rounded to whole cents with half-up rounding so money never carries a
 * fractional tail.
 */
export function lineTotalCents(quantity: number, unitPriceCents: Cents): Cents {
  assertCents(unitPriceCents, 'unitPriceCents');
  if (typeof quantity !== 'number' || !Number.isFinite(quantity)) {
    throw new MoneyError(`quantity must be a finite number, got ${String(quantity)}`);
  }
  if (quantity < 0) throw new MoneyError('quantity must not be negative');
  return roundHalfUp(quantity * unitPriceCents);
}

export function sumCents(values: readonly Cents[]): Cents {
  let total = 0;
  for (const v of values) {
    assertCents(v, 'line total');
    total += v;
  }
  if (!Number.isSafeInteger(total)) throw new MoneyError('sum exceeds safe integer range');
  return total;
}

/**
 * Tax on a subtotal. `rateBasisPoints` is hundredths of a percent
 * (e.g. 9.45% => 945) so the rate itself is an exact integer.
 */
export function taxCents(subtotalCents: Cents, rateBasisPoints: number): Cents {
  assertCents(subtotalCents, 'subtotalCents');
  if (!Number.isInteger(rateBasisPoints) || rateBasisPoints < 0) {
    throw new MoneyError('rateBasisPoints must be a non-negative integer');
  }
  return roundHalfUp((subtotalCents * rateBasisPoints) / 10_000);
}

/** Half-up away from zero, matching how invoices are conventionally rounded. */
export function roundHalfUp(value: number): Cents {
  if (!Number.isFinite(value)) throw new MoneyError('cannot round a non-finite value');
  const rounded = value < 0 ? -Math.round(-value) : Math.round(value);
  if (!Number.isSafeInteger(rounded))
    throw new MoneyError('rounded value exceeds safe integer range');
  return rounded;
}

/** Display only. Never parse this back into a stored amount. */
export function formatCents(cents: Cents): string {
  assertCents(cents);
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100);
  const remainder = String(abs % 100).padStart(2, '0');
  return `${negative ? '-' : ''}$${dollars.toLocaleString('en-US')}.${remainder}`;
}

/** Parse user input like "1,234.56" or "$45" into cents. Returns null if unparseable. */
export function parseCents(input: string): Cents | null {
  const cleaned = input.trim().replace(/[$,\s]/g, '');
  if (cleaned === '' || !/^-?\d*(\.\d{0,2})?$/.test(cleaned)) return null;
  const negative = cleaned.startsWith('-');
  const [whole = '0', frac = ''] = cleaned.replace('-', '').split('.');
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, '0') || '0');
  if (!Number.isSafeInteger(cents)) return null;
  return negative ? -cents : cents;
}
